import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DateInput from "@/components/DateInput";
import api, { API_URL, toErrMsg } from "@/lib/api";
import { fmtDate } from "@/lib/dt";
import { useEngagement } from "@/contexts/EngagementContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import AttachmentPanel from "@/components/AttachmentPanel";
import {
  Plus, Download, Trash2, Pencil, Paperclip, Upload, ExternalLink,
} from "lucide-react";
import { LockedBanner } from "@/components/SignOffCard";
import { useI18n } from "@/contexts/I18nContext";
import { fmtPlural } from "@/lib/format";
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter

const CATEGORIES = [
  { key: "ap", labelKey: "confirmationsPage.categories.ap" },
  { key: "ar", labelKey: "confirmationsPage.categories.ar" },
  { key: "related_party", labelKey: "confirmationsPage.categories.related_party" },
  { key: "bank", labelKey: "confirmationsPage.categories.bank" },
  { key: "other", labelKey: "confirmationsPage.categories.other" },
];
const STATUSES = ["Not Sent", "Sent", "Received – Agreed",
  "Received – Discrepancy", "No Reply", "Second Request Sent"];
const TYPES = ["Positive", "Negative", "Blank"];
const FOLLOW_UPS = ["None", "Send reminder", "Escalate", "Alt. procedure", "Waive"];
const BANK_COVERAGE = ["Balance", "Loans", "Overdraft", "Guarantees",
  "Signatories", "Undrawn Facilities"];
const RP_RELATIONSHIPS = ["Parent", "Subsidiary", "Associate",
  "Key Management", "Common Control", "Other"];

const statusColor = (s) => ({
  "Not Sent": "bg-slate-100 text-slate-700",
  "Sent": "bg-blue-100 text-blue-800",
  "Received – Agreed": "bg-emerald-100 text-emerald-800",
  "Received – Discrepancy": "bg-red-100 text-red-800",
  "No Reply": "bg-amber-100 text-amber-800",
  "Second Request Sent": "bg-emerald-100 text-emerald-800",
}[s] || "bg-slate-100 text-slate-700");

const fmtNum = (n) => (n == null || Number.isNaN(Number(n)))
  ? "—"
  : fmtNumber(Number(n));

export default function Confirmations() {
  const { lang, dict, t } = useI18n();
  const { current } = useEngagement();
  const [tab, setTab] = useState("ap");
  const [rows, setRows] = useState([]);
  const confCols = useResizableColumns({ storageKey: "confirmations", defaults: [100, 220, 100, 130, 130, 130, 120, 120, 120, 130, 70, 160], mins: [80, 140, 80, 100, 100, 100, 90, 96, 96, 96, 56, 120] });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const locked = !!current?.signed_off_at;

  const engId = current?.id;
  const load = useCallback(async () => {
    if (!engId) return;
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get(`/confirmations/${engId}`),
        api.get(`/confirmations/${engId}/summary`),
      ]);
      setRows(r.data || []);
      setSummary(s.data || null);
    } finally { setLoading(false); }
  }, [engId]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => rows.filter((r) => r.category === tab), [rows, tab]);

  const openNew = () => { setEditing({ category: tab }); setDialogOpen(true); };
  const openEdit = (row) => { setEditing({ ...row }); setDialogOpen(true); };

  const deleteRow = async (row) => {
    try {
      await api.delete(`/confirmations/${row.id}`);
      toast.success(t("confirmationsPage.toastDeleted"));
      await load();
    } catch (e) { toast.error(toErrMsg(e, t("confirmationsPage.toastDeleteFailed"))); }
  };

  const exportXlsx = async () => {
    const token = localStorage.getItem("af_token");
    const url = `${API_URL}/confirmations/${current.id}/export`;
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `confirmations_${current.engagement_name || current.client_name || current.id}.xlsx`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!current) {
    return <div className="text-sm text-muted-foreground">
      {t("confirmationsPage.noEngagement")}
    </div>;
  }

  const overall = summary?.overall || {};

  return (
    <div className="space-y-4" data-testid="confirmations-page">
      <LockedBanner engagement={current} />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("confirmationsPage.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("confirmationsPage.subtitle")}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={exportXlsx}
                  data-testid="conf-export-btn">
          <Download className="me-1 h-4 w-4" /> {t("confirmationsPage.exportBtn")}
        </Button>
      </div>

      {/* Header strip — engagement-wide status counts */}
      <Card>
        <CardContent className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 md:grid-cols-6">
          <StatusPill label={t("confirmationsPage.totalLabel")} value={summary?.total ?? 0} tone="slate"
                       testid="conf-sum-total" />
          {STATUSES.map((s) => (
            <StatusPill key={s} label={t(`confirmationsPage.statuses.${s}`) || s} value={overall[s] ?? 0}
                          tone={s === "Received – Agreed" ? "emerald"
                              : s === "Received – Discrepancy" ? "red"
                              : s === "No Reply" ? "amber"
                              : s === "Sent" ? "blue"
                              : s === "Second Request Sent" ? "indigo"
                              : "slate"}
                          testid={`conf-sum-${s.replace(/\W+/g, "_")}`} />
          ))}
        </CardContent>
      </Card>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-1 border-b" data-testid="conf-tabs">
        {CATEGORIES.map((c) => {
          const active = tab === c.key;
          const count = summary?.by_category?.[c.key]?.count ?? 0;
          return (
            <button
              key={c.key} type="button"
              data-testid={`conf-tab-${c.key}`}
              onClick={() => setTab(c.key)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm transition-colors ${
                active
                  ? "border-emerald-600 font-semibold text-emerald-700"
                  : "border-transparent text-muted-foreground hover:text-slate-900 dark:hover:text-slate-100"
              }`}>
              {c.labelKey ? t(c.labelKey) : c.key}
              <span className="ms-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] tabular-nums text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Add + table */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {t("confirmationsPage.rowsInLabel")
            .replace("{plural}", fmtPlural(filtered.length, lang, dict.plurals.confirmations))
            .replace("{cat}", t(`confirmationsPage.categories.${tab}`) || tab)}
        </div>
        <Button onClick={openNew} size="sm" disabled={locked}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="conf-add-btn">
          <Plus className="me-1 h-3 w-3" /> {t("confirmationsPage.addBtn")}
        </Button>
      </div>

      <Card>
        <CardContent className="table-sticky-box overflow-x-auto p-0">
          <ResizableTable cols={confCols} className="text-sm" data-testid="confirmations-table">
            <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground dark:bg-slate-900/40">
              <tr>
                <ResizableTh index={0} className="px-3 py-2 text-start">{t("confirmationsPage.cols.ref")}</ResizableTh>
                <ResizableTh index={1} className="px-3 py-2 text-start">{t("confirmationsPage.cols.counterparty")}</ResizableTh>
                <ResizableTh index={2} className="px-3 py-2 text-start">{t("confirmationsPage.cols.type")}</ResizableTh>
                <ResizableTh index={3} className="px-3 py-2 text-end">{t("confirmationsPage.cols.balance")}</ResizableTh>
                <ResizableTh index={4} className="px-3 py-2 text-end">{t("confirmationsPage.cols.confirmed")}</ResizableTh>
                <ResizableTh index={5} className="px-3 py-2 text-end">{t("confirmationsPage.cols.discrepancy")}</ResizableTh>
                <ResizableTh index={6} className="px-3 py-2 text-start">{t("confirmationsPage.cols.status")}</ResizableTh>
                <ResizableTh index={7} className="px-3 py-2 text-start">{t("confirmationsPage.cols.sent")}</ResizableTh>
                <ResizableTh index={8} className="px-3 py-2 text-start">{t("confirmationsPage.cols.received")}</ResizableTh>
                <ResizableTh index={9} className="px-3 py-2 text-start">{t("confirmationsPage.cols.followup")}</ResizableTh>
                <ResizableTh index={10} className="px-3 py-2 text-center">{t("confirmationsPage.cols.doc")}</ResizableTh>
                <ResizableTh index={11} className="px-3 py-2 text-center">{t("confirmationsPage.cols.actions")}</ResizableTh>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={12} className="p-6 text-center text-muted-foreground">
                  {t("confirmationsPage.loading")}
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={12} className="p-6 text-center text-muted-foreground">
                  {t("confirmationsPage.empty")}
                </td></tr>
              )}
              {!loading && filtered.map((r) => (
                <ConfRow key={r.id} row={r} locked={locked}
                            onEdit={() => openEdit(r)}
                            onDelete={() => deleteRow(r)} t={t} />
              ))}
            </tbody>
          </ResizableTable>
        </CardContent>
      </Card>

      <ConfDialog
        open={dialogOpen} onOpenChange={setDialogOpen}
        engagementId={current.id}
        initial={editing}
        onSaved={async () => { setDialogOpen(false); await load(); }}
      />
    </div>
  );
}

function StatusPill({ label, value, tone, testid }) {
  const cls = {
    slate: "text-slate-700 dark:text-slate-200",
    emerald: "text-emerald-700",
    red: "text-red-700",
    amber: "text-amber-700",
    blue: "text-blue-700",
    indigo: "text-emerald-700",
  }[tone] || "text-slate-700";
  return (
    <div className="rounded-md border bg-white p-3 dark:bg-slate-950"
          data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`text-xl font-semibold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}

function ConfRow({ row, locked, onEdit, onDelete, t }) {
  const disc = row.discrepancy_amount;
  return (
    <tr className="border-b hover:bg-slate-50 dark:hover:bg-slate-900/40"
        data-testid={`conf-row-${row.id}`}>
      <td className="px-3 py-2 font-mono text-[11px] text-emerald-700">
        {row.ref_no}
      </td>
      <td className="px-3 py-2 font-medium">
        {row.counterparty}
        {row.counterparty_account && (
          <div className="font-mono text-[10px] text-muted-foreground">
            {row.counterparty_account}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-xs">{row.confirmation_type}</td>
      <td className="px-3 py-2 num-cell">{fmtNum(row.balance_client)}</td>
      <td className="px-3 py-2 num-cell">{fmtNum(row.confirmed_balance)}</td>
      <td className={`px-3 py-2 num-cell ${
        disc && Number(disc) !== 0 ? "text-red-700 font-medium" : ""
      }`}>
        {disc != null ? fmtNum(disc) : "—"}
      </td>
      <td className="px-3 py-2">
        <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${statusColor(row.status)}`}>
          {t(`confirmationsPage.statuses.${row.status}`) || row.status}
        </span>
      </td>
      <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDate(row.date_sent) || "—"}</td>
      <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDate(row.date_received) || "—"}</td>
      <td className="px-3 py-2 text-xs">{t(`confirmationsPage.followUps.${row.follow_up_action || "None"}`) || (row.follow_up_action || t("confirmationsPage.followUps.None"))}</td>
      <td className="px-3 py-2 text-center">
        {row.document_id ? (
          <a href="#" onClick={async (e) => {
            e.preventDefault();
            try {
              const { getDownloadUrl } = await import("@/lib/api");
              const url = await getDownloadUrl(row.document_id, { mode: "preview" });
              window.open(url, "_blank", "noopener,noreferrer");
            } catch (err) { console.warn("download token failed", err); }
          }}
              target="_blank" rel="noreferrer"
              className="inline-flex text-emerald-600 hover:text-emerald-800"
              title={t("confirmationsPage.openAttached")}
              data-testid={`conf-doc-${row.id}`}>
            <Paperclip className="h-3.5 w-3.5" />
          </a>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-center gap-1">
          <button type="button" onClick={onEdit}
                    disabled={locked}
                    className="rounded p-1 text-slate-600 hover:bg-slate-100 disabled:opacity-40 dark:hover:bg-slate-800"
                    data-testid={`conf-edit-${row.id}`} title={t("confirmationsPage.editBtn")}>
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button type="button" disabled={locked}
                        className="rounded p-1 text-red-600 hover:bg-red-50 disabled:opacity-40 dark:hover:bg-red-950/50"
                        data-testid={`conf-delete-${row.id}`} title={t("confirmationsPage.deleteBtn")}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("confirmationsPage.deletePromptTitle")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("confirmationsPage.deletePromptBody")
                    .replace("{ref}", row.ref_no)
                    .replace("{counterparty}", row.counterparty)}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("confirmationsPage.cancelBtn")}</AlertDialogCancel>
                <AlertDialogAction onClick={onDelete}
                                       className="bg-red-600 hover:bg-red-700"
                                       data-testid={`conf-delete-confirm-${row.id}`}>
                  {t("confirmationsPage.deleteConfirmBtn")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </td>
    </tr>
  );
}

function ConfDialog({ open, onOpenChange, engagementId, initial, onSaved }) {
  const { t } = useI18n();
  const isEdit = !!initial?.id;
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    category: "ap", counterparty: "", counterparty_account: "",
    balance_client: 0, confirmation_type: "Positive", status: "Not Sent",
    date_sent: "", date_received: "", confirmed_balance: "",
    discrepancy_notes: "", alternative_procedures: "",
    follow_up_action: "None", coverage: [], relationship: "",
    document_id: null,
  });
  useEffect(() => {
    if (!open) return;
    setForm({
      category: initial?.category || "ap",
      counterparty: initial?.counterparty || "",
      counterparty_account: initial?.counterparty_account || "",
      balance_client: initial?.balance_client ?? 0,
      confirmation_type: initial?.confirmation_type || "Positive",
      status: initial?.status || "Not Sent",
      date_sent: initial?.date_sent || "",
      date_received: initial?.date_received || "",
      confirmed_balance: initial?.confirmed_balance ?? "",
      discrepancy_notes: initial?.discrepancy_notes || "",
      alternative_procedures: initial?.alternative_procedures || "",
      follow_up_action: initial?.follow_up_action || "None",
      coverage: initial?.coverage || [],
      relationship: initial?.relationship || "",
      document_id: initial?.document_id || null,
    });
  }, [open, initial]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleCoverage = (item) => set("coverage",
    form.coverage.includes(item)
      ? form.coverage.filter((x) => x !== item)
      : [...form.coverage, item]);

  const save = async () => {
    if (!form.counterparty.trim()) {
      toast.error(t("confirmationsPage.toastCounterpartyReq")); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        balance_client: Number(form.balance_client || 0),
        confirmed_balance: form.confirmed_balance === "" ? null
                              : Number(form.confirmed_balance),
      };
      if (isEdit) {
        await api.patch(`/confirmations/${initial.id}`, payload);
        toast.success(t("confirmationsPage.toastUpdated"));
      } else {
        await api.post(`/confirmations/${engagementId}`, payload);
        toast.success(t("confirmationsPage.toastCreated"));
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(toErrMsg(e, t("confirmationsPage.toastSaveFailed")));
    } finally { setSaving(false); }
  };

  const onAttach = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("engagement_id", engagementId);
      fd.append("doc_type", "confirmation");
      const r = await api.post("/documents/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      // If editing, immediately persist the attachment link
      if (isEdit) {
        await api.patch(`/confirmations/${initial.id}`,
                            { document_id: r.data.id });
      }
      set("document_id", r.data.id);
      toast.success(t("confirmationsPage.toastAttachUploaded"));
    } catch (err) {
      toast.error(toErrMsg(err, t("confirmationsPage.toastAttachFailed")));
    } finally { setUploading(false); e.target.value = ""; }
  };

  const category = form.category;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t("confirmationsPage.dialogEditTitle").replace("{ref}", initial.ref_no) : t("confirmationsPage.dialogNewTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("confirmationsPage.dialogDesc")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {/* Category selector */}
          <Field label={t("confirmationsPage.fields.category")}>
            <Select value={form.category} onValueChange={(v) => set("category", v)}
                       disabled={isEdit}>
              <SelectTrigger data-testid="conf-cat"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.labelKey ? t(c.labelKey) : c.key}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("confirmationsPage.fields.counterparty")}>
            <Input value={form.counterparty}
                     onChange={(e) => set("counterparty", e.target.value)}
                     data-testid="conf-counterparty"
                     placeholder={t("confirmationsPage.placeholders.counterparty")} />
          </Field>
          <Field label={t("confirmationsPage.fields.counterpartyAccount")}>
            <Input value={form.counterparty_account}
                     onChange={(e) => set("counterparty_account", e.target.value)}
                     data-testid="conf-account" />
          </Field>
          <Field label={t("confirmationsPage.fields.balanceClient")}>
            <Input type="number" step="0.01"
                     value={form.balance_client}
                     onChange={(e) => set("balance_client", e.target.value)}
                     data-testid="conf-balance" />
          </Field>
          <Field label={t("confirmationsPage.fields.type")}>
            <Select value={form.confirmation_type}
                       onValueChange={(v) => set("confirmation_type", v)}>
              <SelectTrigger data-testid="conf-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map((v) => <SelectItem key={v} value={v}>{t(`confirmationsPage.types.${v}`) || v}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("confirmationsPage.fields.status")}>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="conf-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((v) => <SelectItem key={v} value={v}>{t(`confirmationsPage.statuses.${v}`) || v}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("confirmationsPage.fields.dateSent")}>
            <DateInput value={form.date_sent}
                     onChange={(v) => set("date_sent", v)}
                     data-testid="conf-date-sent" />
          </Field>
          <Field label={t("confirmationsPage.fields.dateReceived")}>
            <DateInput value={form.date_received}
                     onChange={(v) => set("date_received", v)}
                     data-testid="conf-date-received" />
          </Field>
          <Field label={t("confirmationsPage.fields.confirmedBalance")}>
            <Input type="number" step="0.01" placeholder="—"
                     value={form.confirmed_balance}
                     onChange={(e) => set("confirmed_balance", e.target.value)}
                     data-testid="conf-confirmed-balance" />
          </Field>
          <Field label={t("confirmationsPage.fields.followup")}>
            <Select value={form.follow_up_action}
                       onValueChange={(v) => set("follow_up_action", v)}>
              <SelectTrigger data-testid="conf-followup"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FOLLOW_UPS.map((v) => <SelectItem key={v} value={v}>{t(`confirmationsPage.followUps.${v}`) || v}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          {/* Bank-specific */}
          {category === "bank" && (
            <Field label={t("confirmationsPage.fields.coverage")} className="md:col-span-2">
              <div className="flex flex-wrap gap-2 rounded-md border p-2">
                {BANK_COVERAGE.map((c) => (
                  <label key={c}
                            className="flex cursor-pointer items-center gap-1 rounded border bg-slate-50 px-2 py-1 text-xs dark:bg-slate-900">
                    <Checkbox checked={form.coverage.includes(c)}
                                 onCheckedChange={() => toggleCoverage(c)}
                                 data-testid={`conf-coverage-${c}`} />
                    {t(`confirmationsPage.bankCoverage.${c}`) || c}
                  </label>
                ))}
              </div>
            </Field>
          )}
          {/* Related Party-specific */}
          {category === "related_party" && (
            <Field label={t("confirmationsPage.fields.relationship")}>
              <Select value={form.relationship}
                         onValueChange={(v) => set("relationship", v)}>
                <SelectTrigger data-testid="conf-relationship">
                  <SelectValue placeholder={t("confirmationsPage.placeholders.relationshipDash")} />
                </SelectTrigger>
                <SelectContent>
                  {RP_RELATIONSHIPS.map((v) => <SelectItem key={v} value={v}>{t(`confirmationsPage.rpRelationships.${v}`) || v}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label={t("confirmationsPage.fields.discrepancyNotes")} className="md:col-span-2">
            <Textarea rows={2} value={form.discrepancy_notes}
                         onChange={(e) => set("discrepancy_notes", e.target.value)}
                         data-testid="conf-notes"
                         placeholder={t("confirmationsPage.placeholders.discrepancyNotes")} />
          </Field>
          <Field label={t("confirmationsPage.fields.altProc")} className="md:col-span-2">
            <Textarea rows={2} value={form.alternative_procedures}
                         onChange={(e) => set("alternative_procedures", e.target.value)}
                         data-testid="conf-altproc"
                         placeholder={t("confirmationsPage.placeholders.altProc")} />
          </Field>
          {/* Attachment */}
          <Field label={t("confirmationsPage.fields.attachment")} className="md:col-span-2">
            <div className="flex items-center gap-2">
              {form.document_id ? (
                <>
                  <a target="_blank" rel="noreferrer"
                      href="#" onClick={async (e) => {
                        e.preventDefault();
                        try {
                          const { getDownloadUrl } = await import("@/lib/api");
                          const url = await getDownloadUrl(form.document_id, { mode: "preview" });
                          window.open(url, "_blank", "noopener,noreferrer");
                        } catch (err) { console.warn("download token failed", err); }
                      }}
                      className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline"
                      data-testid="conf-attach-open">
                    <Paperclip className="h-3 w-3" /> {t("confirmationsPage.viewAttached")}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                  <Button size="sm" variant="ghost"
                            onClick={() => set("document_id", null)}
                            data-testid="conf-attach-clear">
                    <Trash2 className="h-3 w-3 me-1" /> {t("confirmationsPage.remove")}
                  </Button>
                </>
              ) : (
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-900">
                  <Upload className="h-3 w-3" />
                  {uploading ? t("confirmationsPage.uploadingLabel") : t("confirmationsPage.uploadPdfImg")}
                  <input type="file" className="hidden"
                            accept=".pdf,image/*" onChange={onAttach}
                            disabled={uploading}
                            data-testid="conf-attach-input" />
                </label>
              )}
            </div>
          </Field>
          {/* v55 — general supporting attachments linked to THIS confirmation
              (not just the engagement). Only when editing an existing row —
              a brand-new row has no confirmation id yet. */}
          {isEdit && initial.id && (
            <div className="md:col-span-2">
              <AttachmentPanel
                engagementId={engagementId}
                contextType="confirmation"
                contextId={initial.id}
                label={t("confirmationsPage.fields.supportingDocs")}
                compact
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline"
                    onClick={() => onOpenChange(false)}>{t("confirmationsPage.cancelBtn")}</Button>
          <Button onClick={save} disabled={saving}
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid="conf-save">
            {saving ? t("confirmationsPage.savingLabel") : (isEdit ? t("confirmationsPage.saveChanges") : t("confirmationsPage.createBtn"))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className }) {
  return (
    <div className={className}>
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
